import React, { useState } from 'react';
import { Product } from '../db';
import { productsAPI } from '../backend';
import { Edit } from 'lucide-react';
import { brandsOf, NO_BRAND } from '../utils/brands';

function BrandCard({ name, count, onSelect, onRename, renaming }: {
  name: string;
  count: number;
  onSelect?: (brand: string) => void;
  onRename?: () => void;
  renaming: boolean;
}) {
  const selectable = !!onSelect;

  return (
    <div
      className={`client-card${selectable ? ' brand-card--selectable' : ''}`}
      onClick={selectable ? () => onSelect!(name) : undefined}
      onKeyDown={selectable ? e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect!(name);
        }
      } : undefined}
      role={selectable ? 'button' : undefined}
      tabIndex={selectable ? 0 : undefined}
      title={selectable ? `Ver productos de ${name}` : undefined}
    >
      <div className="client-info">
        <h3 className="client-item name">{name}</h3>
        <p className="client-item brand-row-count">{count} producto{count === 1 ? '' : 's'}</p>
      </div>
      {onRename && (
        <div className="client-actions">
          <button
            className="btn-secondary btn-small"
            onClick={e => { e.stopPropagation(); onRename(); }}
            disabled={renaming}
            title="Renombrar marca"
          >
            <Edit size={16} />
          </button>
        </div>
      )}
    </div>
  );
}

interface BrandsViewProps {
  products: Product[];
  onBack: () => void;
  // Called after a rename so the parent can refresh its product list.
  // Only needed alongside canRename.
  onChanged?: () => Promise<void> | void;
  // Renaming/merging brands is an inventory-management action, so it is
  // opt-in — the catálogo uses this view purely to pick a brand.
  canRename?: boolean;
  // When provided, each brand card becomes a shortcut that filters by it
  onSelectBrand?: (brand: string) => void;
}

function BrandsView({ products, onBack, onChanged, canRename = false, onSelectBrand }: BrandsViewProps) {
  const [renaming, setRenaming] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const brands = brandsOf(products);
  const unbrandedCount = products.filter(p => !p.brand).length;

  const handleRenameBrand = async (oldName: string) => {
    const input = window.prompt(`Renombrar "${oldName}" a:`, oldName);
    const newName = input?.trim();
    if (!newName || newName === oldName) return;

    const affected = products.filter(p => p.brand === oldName);
    const mergeWarning = brands.includes(newName)
      ? ` Se unificará con la marca existente "${newName}".`
      : '';
    if (!window.confirm(`Se aplicará a ${affected.length} producto(s).${mergeWarning}`)) return;

    setRenaming(true);
    setError(null);
    try {
      await Promise.all(affected.map(p => productsAPI.update(p.id, { brand: newName })));
      await onChanged?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error renombrando marca');
    } finally {
      setRenaming(false);
    }
  };

  return (
    <div className="main-view">
      <div className="products-header">
        <h1>Marcas</h1>
        <p>{canRename ? 'Administra las marcas de tu catálogo' : 'Selecciona una marca para filtrar el catálogo'}</p>
      </div>

      <div className="products-controls">
        <button className="btn-secondary" onClick={onBack} disabled={renaming}>
          Regresar
        </button>
      </div>

      {error && <div className="error">Error: {error}</div>}

      <div className="clients-grid">
        {brands.map(brand => (
          <BrandCard
            key={brand}
            name={brand}
            count={products.filter(p => p.brand === brand).length}
            onSelect={onSelectBrand}
            onRename={canRename ? () => handleRenameBrand(brand) : undefined}
            renaming={renaming}
          />
        ))}
        {unbrandedCount > 0 && (
          <BrandCard
            name={NO_BRAND}
            count={unbrandedCount}
            onSelect={onSelectBrand}
            renaming={renaming}
          />
        )}
      </div>

      {brands.length === 0 && unbrandedCount === 0 && (
        <div className="no-products">
          <h3>No hay productos</h3>
        </div>
      )}
    </div>
  );
}

export default BrandsView;
